/* eslint-disable max-lines */

import { FeatureId } from '@/config/access-control';

export type Tier = 'basic' | 'professional' | 'elite' | 'admin';

/**
 * A single navigation entry. This schema is the one source of truth for every
 * navigation item in the app: the section sidebars (Regulation, CourtCase,
 * Workspace, ...) read their groups from `secondary`, path-based access checks
 * resolve items through `navigation-utils`, and the flat top-level sidebar is
 * a derived view over the same items (see FLAT_NAVIGATION_SCHEMA).
 */
export interface NavItemSchema {
  id: string;
  href: string;
  label: string;
  icon?: string;
  tier: Tier;
  featureId?: FeatureId;
  relatedPaths?: string[];
  description: string;
  badge?: string;
  companyOnly?: boolean;
  isAlpha?: boolean;
  /**
   * When true, users whose role lacks `featureId` do not see the item at all
   * (no lock, no upgrade dialog), unlike tier-locked items. Admins bypass it.
   */
  hideWithoutFeature?: boolean;
  /** Optional PostHog feature flag key. When set, the item is only shown if the flag is enabled (admins bypass this gate). */
  betaFeature?: string;
  /**
   * When true, tier-locked users still navigate to the real href instead of
   * getting the upgrade dialog — the destination page renders its own
   * helpful upsell. Login gating is unaffected.
   */
  lockedNavigates?: boolean;
  /** When true the item is hidden from every sidebar, including for admins. */
  hidden?: boolean;
}

export interface NavGroupSchema {
  label: string;
  description?: string;
  items: NavItemSchema[];
  /** Renders as a collapsible dropdown when true. Defaults to expanded list. */
  collapsible?: boolean;
  /** Header icon for collapsible groups, rendered like a nav item icon. */
  icon?: string;
  /** Muted section caption rendered above the group, uppercase unless {@link plainEyebrow}. */
  eyebrow?: string;
  /** Renders the eyebrow in sentence case, as the legal-division sidebar does. */
  plainEyebrow?: boolean;
}

/**
 * Alias kept for components that used to import the standalone flat schema
 * types. The flat navigation is now derived from this single schema, so the
 * item shape is identical.
 */
export type FlatNavItem = NavItemSchema;

/** @see FlatNavItem */
export type FlatNavGroup = NavGroupSchema;

export interface NavigationSchema {
  primary: {
    groups: NavGroupSchema[];
  };
  secondary: {
    home: { groups: NavGroupSchema[]; showOnHome?: boolean };
    regulation: { groups: NavGroupSchema[]; showOnHome?: boolean };
    trademark: { groups: NavGroupSchema[]; showOnHome?: boolean };
    courtCase: { groups: NavGroupSchema[]; showOnHome?: boolean };
    workspace: { groups: NavGroupSchema[]; showOnHome?: boolean };
    tabularReview: { groups: NavGroupSchema[]; showOnHome?: boolean };
  };
}

/**
 * Canonical navigation schema — the single source of truth for every
 * navigation item. Section sidebars read their groups from `secondary`, the
 * legacy sidebar reads `primary`, and the flat sidebar is built from this same
 * registry via FLAT_NAVIGATION_SCHEMA.
 */
export const NAVIGATION_SCHEMA: NavigationSchema = {
  primary: {
    groups: [
      {
        label: 'Beranda',
        description: 'Dashboard utama Justisio.',
        items: [
          {
            id: 'navigation-home',
            href: '/id/home',
            label: 'Beranda',
            icon: 'home',
            tier: 'basic',
            description: 'Dashboard utama Justisio',
            relatedPaths: ['/id/home'],
          },
        ],
      },
      {
        label: 'Regulasi',
        description: 'Cari, ringkas, dan konsolidasi peraturan Indonesia.',
        items: [
          {
            id: 'navigation-regulation',
            href: '/id/regulation',
            label: 'Regulasi',
            tier: 'basic',
            description: 'Navigasi ke halaman Regulasi',
            relatedPaths: ['/id/regulation'],
          },
        ],
      },
      {
        label: 'Putusan Pengadilan',
        description: 'Telusuri putusan pengadilan Indonesia.',
        items: [
          {
            id: 'navigation-court-case',
            href: '/id/court-case',
            label: 'Putusan Pengadilan',
            tier: 'basic',
            description: 'Navigasi ke halaman Putusan Pengadilan',
            relatedPaths: ['/id/court-case'],
          },
          {
            id: 'navigation-putusan',
            href: '/id/court-case/putusan',
            label: 'Preseden',
            tier: 'professional',
            featureId: 'precedent',
            description: 'Penelusuran putusan pengadilan dan yurisprudensi hukum Indonesia',
            relatedPaths: ['/id/court-case/putusan', '/id/court-case/putusan/precedent', '/id/court-case/putusan/jurisprudence'],
          },
        ],
      },
      {
        label: 'Merek',
        description: 'Eksplorasi dan klasifikasi merek Indonesia.',
        items: [
          {
            id: 'trademark-explorer',
            href: '/id/trademark',
            label: 'Merek',
            tier: 'basic',
            description: 'Navigasi ke halaman Merek',
            relatedPaths: ['/id/trademark'],
            badge: 'Alpha',
            isAlpha: true,
          },
        ],
      },
      {
        label: 'Workspace',
        description: 'Manage files and review documents.',
        items: [
          {
            id: 'navigation-workspace',
            href: '/id/workspace',
            label: 'Workspace',
            tier: 'professional',
            featureId: 'workspace',
            description: 'Manage files and review documents in workspace',
            relatedPaths: ['/id/workspace'],
          },
        ],
      },
    ],
  },
  secondary: {
    home: {
      groups: [
        {
          label: 'Jelajahi Fitur',
          items: [
            {
              id: 'home-regulation',
              href: '/id/regulation',
              label: 'Regulasi',
              icon: 'account_balance',
              tier: 'basic',
              description: 'Eksplorasi database peraturan Indonesia',
            },
            {
              id: 'home-court-case',
              href: '/id/court-case',
              label: 'Putusan Pengadilan',
              icon: 'gavel',
              tier: 'basic',
              description: 'Telusuri putusan pengadilan',
            },
            {
              id: 'home-trademark',
              href: '/id/trademark',
              label: 'Merek',
              icon: 'verified',
              tier: 'basic',
              description: 'Data dan klasifikasi merek',
            },
          ],
        },
      ],
      showOnHome: false,
    },
    regulation: {
      groups: [
        {
          label: 'Riset',
          items: [
            {
              // Only reachable from the regulation package sidebar: what is
              // new in the member's fields and how long their access runs.
              id: 'reg-dashboard',
              href: '/id/regulation/dashboard',
              label: 'Dashboard',
              icon: 'dashboard',
              tier: 'basic',
              featureId: 'regulation-dashboard',
              hideWithoutFeature: true,
              hidden: true,
              description: 'Peraturan baru di bidang Anda dan masa aktif paket',
            },
            {
              id: 'reg-search',
              href: '/id/regulation/search',
              label: 'Cari Peraturan',
              icon: 'search',
              tier: 'basic',
              description: 'Cari dan analisis peraturan Indonesia',
            },
            {
              id: 'reg-browse',
              href: '/id/regulation/browse',
              label: 'Pusat Data',
              icon: 'library_books',
              tier: 'basic',
              description: 'Jelajahi semua peraturan dengan filter lengkap',
            },
            {
              id: 'reg-research-agent',
              href: '/id/regulation/research-agent',
              label: 'Riset Cepat (Jivara)',
              icon: 'auto_awesome',
              tier: 'professional',
              featureId: 'research-agent',
              description: 'Asisten AI untuk riset peraturan dengan cepat',
              badge: 'PRO',
            },
          ],
        },
        {
          label: 'Manajemen',
          items: [
            {
              id: 'reg-library',
              href: '/id/regulation/library',
              label: 'Bookmark',
              icon: 'bookmark',
              tier: 'basic',
              description: 'Koleksi peraturan yang Anda simpan',
            },
            {
              id: 'reg-history',
              href: '/id/regulation/search-history',
              label: 'Riwayat',
              icon: 'history',
              tier: 'basic',
              description: 'Aktivitas riset peraturan Anda',
            },
            {
              id: 'reg-notification',
              href: '/id/regulation/notification',
              label: 'Notifikasi Regulasi',
              icon: 'notifications',
              tier: 'professional',
              featureId: 'company-regulation-notification',
              description: 'Notifikasi regulasi terbaru untuk perusahaan Anda',
              relatedPaths: ['/id/regulation/notification'],
              badge: 'Untuk Perusahaan',
              companyOnly: true,
            },
          ],
        },
      ],
    },
    trademark: {
      groups: [
        {
          label: 'Data',
          items: [
            {
              id: 'tm-explorer',
              href: '/id/trademark/explorer',
              label: 'Merek',
              icon: 'verified',
              tier: 'basic',
              description: 'Eksplorasi klasifikasi barang/jasa untuk merek',
              badge: 'Alpha',
              isAlpha: true,
            },
          ],
        },
        {
          label: 'AI Agent',
          items: [
            {
              id: 'tm-classification',
              href: '/id/trademark/classification-agent',
              label: 'Klasifikasi Merek',
              icon: 'auto_awesome',
              tier: 'professional',
              featureId: 'trademark-classification',
              description: 'Otomatisasi pencarian kelas merek dengan AI',
              badge: 'AI Agent',
            },
          ],
        },
        // {
        //   label: 'Mock',
        //   items: [
        //     {
        //       id: 'tm-mock-classification',
        //       href: '/id/trademark/mock/classification-agent',
        //       label: 'Klasifikasi Merek (Mock)',
        //       tier: 'basic',
        //       featureId: 'trademark-mock',
        //       description: 'Prototipe klasifikasi merek (admin only)',
        //     },
        //   ],
        // },
      ],
    },
    courtCase: {
      groups: [
        {
          label: 'Riset',
          items: [
            {
              id: 'cc-constitutional',
              href: '/id/court-case/constitutional-court',
              label: 'Putusan MK',
              icon: 'account_balance',
              tier: 'basic',
              description: 'Penelusuran putusan Mahkamah Konstitusi',
            },
            {
              id: 'cc-putusan',
              href: '/id/court-case/putusan',
              label: 'Preseden',
              icon: 'gavel',
              tier: 'professional',
              featureId: 'precedent',
              description: 'Penelusuran putusan pengadilan dan yurisprudensi hukum Indonesia',
              badge: 'PRO',
            },
          ],
        },
      ],
    },
    workspace: {
      groups: [
        {
          label: 'Manajemen',
          items: [
            {
              id: 'cases-files',
              href: '/id/workspace/cases',
              label: 'Workspace',
              icon: 'folder',
              tier: 'professional',
              featureId: 'workspace',
              description: 'Kelola file dokumen hukum dalam perkara',
              badge: 'PRO',
            },
            {
              id: 'workspace-templates',
              href: '/id/workspace/templates',
              label: 'Template Dokumen',
              icon: 'description',
              tier: 'professional',
              featureId: 'workspace',
              description: 'Dokumen Word atau PDF milik kantor yang bisa dipakai di Jivara',
            },
            {
              id: 'workspace-checklists',
              href: '/id/workspace/checklists',
              label: 'Checklist',
              icon: 'assignment_turned_in',
              tier: 'basic',
              featureId: 'compliance-checklist',
              hideWithoutFeature: true,
              description: 'Semua checklist kepatuhan di workspace ini, per perkara',
              badge: 'Trial',
            },
            {
              id: 'workspace-regulation-checklist',
              href: '/id/workspace/regulation-checklist',
              label: 'Compliance Checklist',
              icon: 'assignment_turned_in',
              tier: 'basic',
              featureId: 'compliance-checklist',
              hideWithoutFeature: true,
              description: 'Checklist kepatuhan per KBLI dari regulasi terkait',
              badge: 'Trial',
            },
            {
              id: 'workflow-builder',
              href: '/id/workspace/workflow',
              label: 'Workflow',
              icon: 'account_tree',
              tier: 'basic',
              description: 'Buat dan jalankan workflow otomatis untuk pekerjaan hukum',
              badge: 'Alpha',
              isAlpha: true,
            },
            {
              id: 'legal-register',
              href: '/id/workspace/legal-register',
              label: 'Legal Register',
              icon: 'inventory_2',
              tier: 'basic',
              featureId: 'legal-register',
              hideWithoutFeature: true,
              description: 'Daftar izin, aset, dan kontrak beserta masa berlakunya',
              badge: 'Alpha',
              isAlpha: true,
            },
            {
              // Only reachable from the legal-division sidebar: a workspace
              // overview of register items and cases for the legal team.
              id: 'workspace-dashboard',
              href: '/id/workspace/dashboard',
              label: 'Dashboard',
              icon: 'dashboard',
              tier: 'basic',
              featureId: 'legal-register',
              hideWithoutFeature: true,
              hidden: true,
              description: 'Ringkasan izin, aset, kontrak, dan perkara di workspace',
            },
            {
              // Only reachable from the legal-division sidebar; everyone else
              // finds the same list as a tab on the Legal Register page.
              id: 'legal-register-early-warning',
              href: '/id/workspace/peringatan-dini',
              label: 'Peringatan Dini',
              icon: 'notifications_active',
              tier: 'basic',
              featureId: 'legal-register',
              hideWithoutFeature: true,
              hidden: true,
              description: 'Izin, aset, dan kontrak yang akan atau sudah habis masa berlakunya',
            },
            {
              id: 'reminder',
              href: '/id/workspace/reminder',
              label: 'Reminder',
              icon: 'alarm',
              tier: 'basic',
              description: 'Jadwalkan pengingat email otomatis',
              badge: 'Alpha',
              isAlpha: true,
            },
          ],
        },
        {
          label: 'Analisis & AI',
          items: [
            {
              id: 'tabular-reviews',
              href: '/id/workspace/tabular-reviews',
              label: 'Tabel Ekstraksi',
              icon: 'table_chart',
              tier: 'professional',
              featureId: 'tabular-review',
              description: 'Ekstrak data terstruktur dari dokumen hukum',
              badge: 'PRO',
            },
            {
              id: 'document-review',
              href: '/id/workspace/document-review',
              label: 'Tinjau Dokumen',
              icon: 'fact_check',
              tier: 'professional',
              featureId: 'document-review',
              description: 'Analisis kepatuhan regulasi dan kontradiksi dokumen',
              badge: 'PRO',
            },
            {
              id: 'workspace-assistant',
              href: '/id/workspace/assistant',
              label: 'Drafting',
              icon: 'edit_note',
              tier: 'professional',
              featureId: 'workspace-assistant',
              description: 'Asisten AI untuk membuat dan menyusun draf dokumen hukum',
              badge: 'Beta',
              isAlpha: false,
              betaFeature: 'workspace-assistant-beta',
              // Locked users navigate through: the page shows a helpful
              // upsell (regulation search + trial extension) for them.
              lockedNavigates: true,
            },
          ],
        },
      ],
      showOnHome: false,
    },
    tabularReview: {
      groups: [
        {
          label: '',
          items: [
            {
              id: 'tabular-reviews',
              href: '/id/workspace/tabular-reviews',
              label: 'Tabel Ekstraksi',
              icon: 'table_chart',
              tier: 'professional',
              featureId: 'tabular-review',
              description: 'Ekstrak data terstruktur dari dokumen hukum',
              badge: 'PRO',
            },
          ],
        },
      ],
      showOnHome: false,
    },
  },
};

/**
 * Items that appear only in the flat top-level sidebar, not in any section
 * sidebar. They live here so every item in the app is defined exactly once.
 */
const FLAT_ONLY_ITEMS: NavItemSchema[] = [
  {
    id: 'my-consolidations',
    href: '/akun/konsolidasi',
    label: 'Konsolidasi Saya',
    icon: 'layers',
    tier: 'basic',
    featureId: 'my-purchases',
    // Only buyers have something here; everyone else never sees the entry.
    hideWithoutFeature: true,
    description: 'Konsolidasi peraturan yang sudah Anda beli',
    relatedPaths: ['/akun/konsolidasi'],
  },
  {
    id: 'reg-consolidation',
    href: '/id/regulation/consolidation',
    label: 'Konsolidasi',
    icon: 'layers',
    tier: 'professional',
    featureId: 'consolidation-library',
    description: 'Peraturan dasar beserta seluruh perubahannya dalam satu teks terpadu',
    relatedPaths: ['/id/regulation/consolidation'],
  },
  {
    id: 'ma-extension-directory',
    href: '/id/case-law-directory',
    label: 'Direktori Putusan',
    icon: 'gavel',
    tier: 'professional',
    featureId: 'ma-search-directory',
    description: 'Direktori putusan Mahkamah Agung via ekstensi',
    badge: 'PRO',
  },
  {
    id: 'document-tool',
    href: '/id/document-tool',
    label: 'Tools Dokumen',
    icon: 'description',
    tier: 'professional',
    featureId: 'document-tool',
    description: 'Gabungkan, pisahkan, putar, atur halaman, dan kompres file PDF',
    badge: 'PRO',
  },
  {
    id: 'tutorial',
    href: '/id/tutorial',
    label: 'Tutorial',
    icon: 'school',
    tier: 'basic',
    description: 'Panduan fitur Justisio dan video tutorial',
    relatedPaths: ['/id/tutorial'],
  },
];

/** Id → item lookup covering every item in the canonical schema. */
const NAV_ITEM_INDEX = new Map<string, NavItemSchema>();

for (const groups of [
  NAVIGATION_SCHEMA.primary.groups,
  ...Object.values(NAVIGATION_SCHEMA.secondary).map((section) => section.groups),
]) {
  for (const group of groups) {
    for (const item of group.items) {
      // First occurrence wins so intentionally duplicated items (e.g. the
      // tabular-reviews entry in both workspace and tabularReview) resolve to
      // the same canonical definition.
      if (!NAV_ITEM_INDEX.has(item.id)) {
        NAV_ITEM_INDEX.set(item.id, item);
      }
    }
  }
}
for (const item of FLAT_ONLY_ITEMS) {
  NAV_ITEM_INDEX.set(item.id, item);
}

/**
 * Looks up a canonical item by id, so derived sidebars (e.g. package-scoped
 * views) reuse the exact hrefs, tiers and featureIds of the full sidebar.
 *
 * @returns The item, or undefined when no item has that id.
 */
export function getNavItemById(id: string): NavItemSchema | undefined {
  return NAV_ITEM_INDEX.get(id);
}

/** Ordered definition of the flat sidebar groups and which items they show. */
interface FlatGroupDef {
  id: string;
  label: string;
  description?: string;
  itemIds: string[];
  /** Muted uppercase section caption rendered above the group. */
  eyebrow?: string;
  /** Renders as a collapsible dropdown when true. Defaults to expanded list. */
  collapsible?: boolean;
  /** Header icon for collapsible groups. */
  icon?: string;
  /** Per-item presentation overrides keyed by item id (e.g. relabeling). */
  overrides?: Record<string, Partial<NavItemSchema>>;
}

/**
 * Flat navigation schema — the only top-level sidebar, shown to every user.
 * Jivara first (default landing), then dropdowns, Tutorial anchored at the
 * very bottom. Cari Peraturan is surfaced again in Menu Utama as a quick
 * shortcut. Item definitions live only in the canonical registry above (plus
 * FLAT_ONLY_ITEMS); this export only picks which items appear, in what
 * order, with presentation overrides applied.
 */
const FLAT_GROUP_DEFS: FlatGroupDef[] = [
  {
    id: 'main-top',
    label: '',
    eyebrow: 'Menu Utama',
    itemIds: ['workspace-assistant', 'reg-search'],
    overrides: { 'workspace-assistant': { label: 'Jivara', badge: undefined } },
  },
  {
    id: 'main-data',
    label: 'Sumber Hukum',
    eyebrow: 'Perkakas',
    itemIds: ['reg-search', 'cc-putusan', 'reg-browse', 'tm-explorer', 'ma-extension-directory'],
    collapsible: true,
    icon: 'storage',
    overrides: { 'tm-explorer': { hidden: true } },
  },
  {
    id: 'main-ai',
    label: 'AI',
    itemIds: ['reg-research-agent', 'tabular-reviews', 'document-review'],
    collapsible: true,
    icon: 'auto_awesome',
    overrides: { 'reg-research-agent': { label: 'Riset Cepat' } },
  },
  {
    id: 'main-workspace',
    label: 'Workspace',
    itemIds: [
      'cases-files',
      'workspace-checklists',
      'workspace-regulation-checklist',
      'workflow-builder',
      'reminder',
      'document-tool',
    ],
    collapsible: true,
    icon: 'folder',
  },
  {
    id: 'main-util',
    label: '',
    eyebrow: 'Lainnya',
    itemIds: ['my-consolidations', 'tutorial'],
  },
];

export const FLAT_NAVIGATION_SCHEMA: FlatNavGroup[] = FLAT_GROUP_DEFS.map((group) => ({
  label: group.label,
  description: group.description,
  collapsible: group.collapsible,
  icon: group.icon,
  eyebrow: group.eyebrow,
  items: group.itemIds.map((id) => {
    const item = NAV_ITEM_INDEX.get(id);
    if (!item) {
      throw new Error(`Flat nav group "${group.id}" references unknown nav item "${id}"`);
    }
    // No override: return the canonical definition itself so shortcuts
    // (e.g. reg-search in two groups) stay reference-identical, not forks.
    const override = group.overrides?.[id];
    return override ? { ...item, ...override } : item;
  }),
}));

/**
 * What the legal-division package calls its pages, in the division's own
 * words. The sidebar, the page titles and the dashboard's pillar cards all
 * read from here, so one page is never called two different things.
 */
export const LEGAL_DIVISION_LABELS = {
  dashboard: 'Dashboard',
  assistant: 'Asisten Hukum',
  documentReview: 'Tinjau Dokumen',
  tabularReview: 'Tabel Ekstraksi',
  templates: 'Template Dokumen',
  regulationSearch: 'Cari Peraturan',
  decisionSearch: 'Cari Putusan',
  earlyWarning: 'Peringatan Dini',
  cases: 'Perkara',
  registers: 'Kontrak, Izin & Aset',
} as const;

/** True for logged-in users, for whom the Jivara home/chat is the default
 * landing. Still used for shell styling, the mobile sheet side, and the
 * home handoff; navigation itself no longer branches on it. */
export function isHomeChatPreview(userEmail?: string | null): boolean {
  return !!userEmail;
}

